import { Box, Grid, Skeleton, Typography, useTheme } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSubscription } from '@apollo/client';
import { styled } from '@mui/system';
import MagicSuggestionMiniCard from './MagicSuggestionMiniCard';
import MagicSuggestionCard from './MagicSuggestionCard';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import {
  ActivityTemplateType,
  IActivity,
  IGetMagicSuggestionsResponse,
  IMagicSuggestionPayload,
  fetchMagicSuggestionsRequest,
  fetchMagicSuggestionsSuccess,
  selectActivityTemplate,
  updateMagicSuggesiosState,
} from '../../../redux/activity/activitySlice';
import { transformActivities } from '../../../utils/utils';
import { slimScrollbarStyles } from '../../../utils/styles';
import './MagicSuggestion.css';
import { SUBSCRIBE_TO_MAGIC_SUGGESTIONS } from '../../../service/graphql/activityQuery';
import { ActivityTypeEnum } from '../../../types/activity';
import createPermissionCheckers from '../../../permissions-validation/createPermissionCheckers';
import { UserPermissionType } from '../../../types/commonTypes';

const renderSkeleton = () => (
  <Skeleton variant="rectangular" sx={{ animationDuration: '.5s', mb: 3 }} height="30%" />
);

const StyledBox = styled(Box)(() => ({
  background:
    'var(--Brand-Cadet-Blue, linear-gradient(138deg, #6599A5 24.11%, #3B667E 83.2%)) !important',
  borderRadius: '50%',
  padding: '12px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  cursor: 'pointer',
}));

function MagicSuggestion() {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const { t } = useTranslation();
  const magicSuggestionLoading = useAppSelector(
    (state) => state.activity.getMagicSuggestionLoading
  );
  const magicSuggestionError = useAppSelector((state) => state.activity.magicSuggestionError);
  const clientId = useAppSelector((state) => state.client.selectedClient?.id);
  const coachId = useAppSelector((state) => state.coach.coach?.id);
  const suggestionState = useAppSelector((state) => state.activity.suggestionState);
  const magicSuggestions = useAppSelector((state) => state.activity.magicSuggestions);
  const [magicSuggestionsForUI, setMagicSuggestionsForUI] = useState<IActivity[]>([]);
  const [subscriptionSkip, setSubscriptionSkip] = useState<boolean>(true);
  const [selectedActivity, setSelectedActivity] = useState<IActivity | null>(null);
  const [magicSuggestionFromServer, setMagicSuggestionFromServer] =
    useState<IGetMagicSuggestionsResponse>();
  const permissionCheckers = createPermissionCheckers();
  const { checkPermissions } = permissionCheckers;

  useEffect(() => {
    if (!checkPermissions(UserPermissionType.MAGIC_SUGGESTIONS_MODULE_GENERATE_SUGGESTIONS)) {
      setSubscriptionSkip(true);
    }
  }, []);

  const { data } = useSubscription<IMagicSuggestionPayload>(SUBSCRIBE_TO_MAGIC_SUGGESTIONS, {
    variables: { magicSuggestionId: magicSuggestions?.id },
    skip: subscriptionSkip,
  });

  const handleMagicSuggestionClick = (magicSuggestion?: IActivity) => {
    if (!magicSuggestion) {
      setSelectedActivity(null);
      dispatch(selectActivityTemplate()); // reset selected template
      return;
    }
    setSelectedActivity(magicSuggestion);
    dispatch(
      selectActivityTemplate({
        type: ActivityTemplateType.MAGIC_SUGGESTION,
        activity: {
          id: magicSuggestion.id,
          name: magicSuggestion.name,
          type: 'magic_suggestion',
          objective: magicSuggestion.objective,
          coverImage: magicSuggestion.coverImage,
          ActivityStep: magicSuggestion.ActivityStep,
          frequency: magicSuggestion.frequency,
          situation: magicSuggestion.situation,
        },
      })
    );
  };

  const setSuggestionsForUI = (suggestionResponse?: IGetMagicSuggestionsResponse) => {
    if (suggestionResponse?.suggestions) {
      const transformedActivities = transformActivities(suggestionResponse?.suggestions.activities);
      if (transformedActivities.length === 0) {
        return;
      }
      setMagicSuggestionsForUI(transformedActivities);
      handleMagicSuggestionClick(transformedActivities[0]);
    } else {
      setMagicSuggestionsForUI([]);
      handleMagicSuggestionClick();
    }
  };

  const setRefreshedSuggestions = () => {
    if (magicSuggestionFromServer) {
      dispatch(fetchMagicSuggestionsSuccess(magicSuggestionFromServer));
    }
  };

  useEffect(() => {
    if (!clientId || !coachId) return;
    dispatch(fetchMagicSuggestionsRequest({ clientId, coachId }));
  }, [clientId, coachId]);

  useEffect(() => {
    if (magicSuggestions) {
      if (!magicSuggestions.is_completed) {
        setSubscriptionSkip(false);
      }
    }
    setSuggestionsForUI(magicSuggestions ?? undefined);
  }, [magicSuggestions]);

  useEffect(() => {
    if (data) {
      setMagicSuggestionFromServer(data.magicSuggestions);
      setSubscriptionSkip(true);
      dispatch(updateMagicSuggesiosState('REFRESH'));
    }
  }, [data]);

  const renderInProgress = () => (
    <Box
      width="100%"
      p="28px"
      display="flex"
      borderBottom={theme.borderStyles.commonBorders}
      borderLeft={theme.borderStyles.commonBorders}
      gap="24px"
      color={theme.palette.primary.light}
      height="100px"
      alignItems="center"
    >
      <Typography variant="bodyRegular" color="primary" textAlign="left">
        {t('activity.magicSuggestion.magicSuggestionInProgress')}
      </Typography>
      <div className="loader" />
    </Box>
  );

  const clickToRefreash = () => (
    <Box
      width="100%"
      p="28px"
      display="flex"
      borderBottom={theme.borderStyles.commonBorders}
      justifyContent="space-between"
      height="100px"
      alignItems="center"
      borderLeft={theme.borderStyles.commonBorders}
      gap={3}
    >
      <Typography variant="bodyRegular" color="primary.main">
        {t('activity.magicSuggestion.magicSuggestionReady')}
      </Typography>
      <StyledBox
        onClick={() => {
          setRefreshedSuggestions();
        }}
      >
        <img src="/assets/icons/eye-large.svg" alt="eye-large" />
      </StyledBox>
    </Box>
  );

  if (magicSuggestionError) {
    return (
      <Box
        display="flex"
        height="100%"
        flexDirection="row"
        gap="20px"
        borderLeft={theme.borderStyles.commonBorders}
        borderRight={theme.borderStyles.commonBorders}
        p="16px"
      >
        <img
          src="/assets/icons/empty-box.svg"
          alt="empty-notes"
          style={{
            width: '48px',
            height: '48px',
          }}
        />
        <Typography variant="bodyRegular" color="primary.dark">
          {t('activity.assignActivity.magicActivity')}
        </Typography>
      </Box>
    );
  }

  if (magicSuggestionLoading) {
    return (
      <Grid
        container
        sx={{
          height: '100%',
        }}
        xs={12}
      >
        <Grid item xs={6} p="16px">
          {Array.from({ length: 3 }).map(renderSkeleton)}
        </Grid>
        <Grid
          item
          xs={6}
          container
          alignItems="center"
          justifyContent="center"
          p="16px"
          borderLeft={theme.borderStyles.commonBorders}
        >
          <Skeleton
            variant="rectangular"
            sx={{ animationDuration: '1s' }}
            height="100%"
            width="100%"
          />
        </Grid>
      </Grid>
    );
  }
  return (
    <>
      <Box flex={1}>
        <Box display="flex">
          {suggestionState === 'CREATING' && renderInProgress()}
          {suggestionState === 'REFRESH' && clickToRefreash()}
        </Box>
        <Box
          display="flex"
          flexDirection="column"
          overflow="auto"
          height="100%"
          sx={slimScrollbarStyles}
          borderRight={theme.borderStyles.commonBorders}
          borderLeft={theme.borderStyles.commonBorders}
        >
          {magicSuggestionsForUI.length > 0 &&
            magicSuggestionsForUI.map((magicSuggestion: IActivity) => (
              <MagicSuggestionMiniCard
                key={magicSuggestion.id}
                name={magicSuggestion.name}
                objective={magicSuggestion.objective}
                frequency={magicSuggestion.frequency as ActivityTypeEnum}
                isSelected={selectedActivity?.id === magicSuggestion.id}
                onClick={() => handleMagicSuggestionClick(magicSuggestion)}
              />
            ))}
        </Box>
      </Box>
      <Box
        flex=".8"
        bgcolor="rgba(255, 221, 200, 0.25)"
        height="100%"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        {selectedActivity && (
          <MagicSuggestionCard
            name={selectedActivity.name}
            activitySteps={selectedActivity.ActivityStep || []}
          />
        )}
      </Box>
    </>
  );
}

export default MagicSuggestion;
